'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useSalon } from '../contexts/SalonContext'
import { savePreferredSalon, fetchPreferredSalonId } from '../services/preferredSalonService'
import { getSelectedSalonId } from '../utils/salonScope'
import { isDemoMode } from '../utils/demoMode'

/**
 * Blocks the app after first signup/login (email or Google) until a branch is chosen.
 * Existing customers with preferred_salon_id already set are not prompted.
 *
 * localStorage alone is not enough — SalonContext auto-defaults to the first branch
 * for browsing, which previously skipped this gate incorrectly.
 */
export default function SelectBranchGate() {
  const { user, profile, loading: authLoading, refreshProfile } = useAuth()
  const { salons, selectSalon, salonReady, loading: salonLoading } = useSalon()
  const [checking, setChecking] = useState(true)
  const [needsSelection, setNeedsSelection] = useState(false)
  const [selectedId, setSelectedId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const preferredFromProfile = profile?.preferred_salon_id || null

  useEffect(() => {
    let mounted = true

    const run = async () => {
      if (authLoading || !salonReady) return

      if (!user) {
        if (mounted) {
          setNeedsSelection(false)
          setChecking(false)
        }
        return
      }

      setChecking(true)
      try {
        let preferred = preferredFromProfile
        if (!preferred) {
          preferred = await fetchPreferredSalonId(user.id)
        }

        // First login (or never saved a preferred branch): always ask.
        // Do not treat SalonContext's auto-default localStorage value as a real choice.
        if (preferred) {
          const match = (salons || []).find((s) => String(s.id) === String(preferred))
          if (match) selectSalon(match)
          if (mounted) {
            setNeedsSelection(false)
            setChecking(false)
          }
          return
        }

        if (mounted) {
          setNeedsSelection(true)
          const storedId = getSelectedSalonId()
          const storedMatch = (salons || []).find(
            (s) => storedId && String(s.id) === String(storedId)
          )
          setSelectedId(storedMatch?.id || salons[0]?.id || '')
          setChecking(false)
        }
      } catch (e) {
        console.error('SelectBranchGate check failed', e)
        if (mounted) setChecking(false)
      }
    }

    void run()
    return () => {
      mounted = false
    }
  }, [user, preferredFromProfile, authLoading, salonReady, salons, selectSalon])

  const branchOptions = useMemo(() => salons || [], [salons])

  const handleConfirm = async () => {
    if (!selectedId) {
      setError('Please choose a branch to continue.')
      return
    }
    setSaving(true)
    setError('')
    const result = await savePreferredSalon(selectedId)
    if (!result.success) {
      setError(result.error || 'Could not save your branch.')
      setSaving(false)
      return
    }
    const match = branchOptions.find((s) => s.id === selectedId)
    if (match) selectSalon(match)
    if (!isDemoMode()) {
      try {
        await refreshProfile?.()
      } catch {
        // optional
      }
    }
    setNeedsSelection(false)
    setSaving(false)
  }

  if (!user || checking || authLoading || salonLoading || !needsSelection) {
    return null
  }

  return (
    <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-slate-900/60 px-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="select-branch-title"
        className="w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col rounded-2xl bg-white p-6 shadow-xl"
      >
        <h2 id="select-branch-title" className="text-xl font-semibold text-slate-900 flex-shrink-0">
          Choose your branch
        </h2>
        <p className="mt-2 text-sm text-slate-600 flex-shrink-0">
          Pick the Timeless Trends location you want to book at. You can change this later from the menu.
        </p>

        <div className="mt-5 flex-1 min-h-0 max-h-72 space-y-2 overflow-y-auto">
          {branchOptions.length === 0 ? (
            <p className="text-sm text-slate-500">No branches available right now. Please try again shortly.</p>
          ) : (
            branchOptions.map((salon) => {
              const active = selectedId === salon.id
              return (
                <button
                  key={salon.id}
                  type="button"
                  onClick={() => setSelectedId(salon.id)}
                  className={`flex w-full items-start gap-3 rounded-xl border px-3 py-3 text-left transition ${
                    active
                      ? 'border-slate-900 bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <span
                    className={`mt-1 h-4 w-4 shrink-0 rounded-full border ${
                      active ? 'border-slate-900 bg-slate-900' : 'border-slate-300'
                    }`}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-900">{salon.name}</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      {salon.city || salon.location || 'Salon location'}
                    </span>
                  </span>
                </button>
              )
            })
          )}
        </div>

        {error ? <p className="mt-3 text-sm text-rose-600 flex-shrink-0">{error}</p> : null}

        <button
          type="button"
          disabled={saving || !selectedId || branchOptions.length === 0}
          onClick={() => void handleConfirm()}
          className="mt-5 w-full flex-shrink-0 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </div>
  )
}
